import React, { useMemo } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { Member } from '@wix/ambassador-members-ng-api/types';
import {
  PaymentMethod,
  PaymentOption,
  ReservedPaymentOptionIds,
} from '../../../../../types/types';
import { PaymentTypeSelection } from './PaymentTypeSelection/PaymentTypeSelection';
import { useSettings } from '@wix/tpa-settings/react';
import { PaymentOptionSelection } from './PaymentOptionSelection/PaymentOptionSelection';
import { ServiceData, SlotService } from '../../../../../utils/state/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../../utils';
import { getPaymentOptions } from '../../../../../utils/payment';

export type PaymentSelectionProps = {
  serviceData: ServiceData;
  memberDetails?: Member;
  numberOfParticipants: number;
  dateRegionalSettingsLocale: string;
  isPricingPlanInstalled: boolean;
};

export const getPaymentSelection = (props: PaymentSelectionProps) => {
  const paymentSelection = renderPaymentSelection(props);

  const shouldShowSection = paymentSelection.length > 0;

  if (shouldShowSection) {
    return <>{paymentSelection}</>;
  }

  return null;
};

export const PaymentSelection: React.FC<PaymentSelectionProps> = (props) => {
  const paymentSelection = renderPaymentSelection(props);

  const shouldShowSection = paymentSelection.length > 0;

  if (shouldShowSection) {
    return <>{paymentSelection}</>;
  }

  return null;
};

const renderPaymentSelection = ({
  serviceData,
  dateRegionalSettingsLocale,
  numberOfParticipants,
  isPricingPlanInstalled,
  memberDetails,
}: PaymentSelectionProps) => {
  return mapToArray<SlotService>(serviceData.slotServices)
    .map((slotService) => {
      const paymentOptionSelection = RenderPaymentOptionSelection({
        slotService,
        dateRegionalSettingsLocale,
        numberOfParticipants,
        isPricingPlanInstalled,
        memberDetails,
      });

      const paymentTypeSelection = renderPaymentTypeSelection({
        slotService,
      });

      if (paymentOptionSelection || paymentTypeSelection) {
        return (
          <>
            {paymentOptionSelection}
            {paymentTypeSelection}
          </>
        );
      } else {
        return null;
      }
    })
    .filter((item) => item);
};

const RenderPaymentOptionSelection = ({
  slotService,
  dateRegionalSettingsLocale,
  numberOfParticipants,
  isPricingPlanInstalled,
  memberDetails,
}: {
  slotService: SlotService;
  numberOfParticipants: number;
  dateRegionalSettingsLocale: string;
  isPricingPlanInstalled: boolean;
  memberDetails?: Member;
}) => {
  const settings = useSettings();
  const { t } = useTranslation();
  const {
    nestedSlot,
    selectedPaymentOption,
    service,
    memberships,
    dynamicPriceInfo,
  } = slotService;

  const dateAndTimeFormatter = useMemo(
    () => new DateTimeFormatter(dateRegionalSettingsLocale),
    [dateRegionalSettingsLocale],
  );

  const paymentOptions: PaymentOption[] = useMemo(
    () =>
      getPaymentOptions({
        servicePayment: service?.payment,
        memberships,
        isPricingPlanInstalled,
        dateAndTimeFormatter,
        numberOfParticipants,
        dateRegionalSettingsLocale,
        isDynamicPreferenceType: dynamicPriceInfo?.isDynamicPreferenceType,
        t,
        settings,
      }),
    [memberDetails?.id, numberOfParticipants, dateRegionalSettingsLocale],
  );

  const shouldShowPaymentMethodSelection = paymentOptions.length > 1;

  if (!shouldShowPaymentMethodSelection) {
    return null;
  }

  return (
    <PaymentOptionSelection
      key={getServiceSlotIdentifier(nestedSlot)}
      paymentOptions={paymentOptions}
      slotService={slotService}
      isPricingPlanInstalled={isPricingPlanInstalled!}
      selectedPaymentOptionId={selectedPaymentOption.id}
      dateRegionalSettingsLocale={dateRegionalSettingsLocale!}
      numberOfParticipants={numberOfParticipants!}
    />
  );
};

const renderPaymentTypeSelection = ({
  slotService,
}: Partial<PaymentSelectionProps> & { slotService: SlotService }) => {
  const { nestedSlot, selectedPaymentOption, service, selectedPaymentType } =
    slotService;
  const shouldShowPaymentTypeSelection =
    service.paymentTypes.includes(PaymentMethod.OFFLINE) &&
    service.paymentTypes.includes(PaymentMethod.ONLINE) &&
    selectedPaymentOption.id === ReservedPaymentOptionIds.SingleSession;

  if (!shouldShowPaymentTypeSelection) {
    return null;
  }

  const componentKey = getServiceSlotIdentifier(nestedSlot);

  return (
    <PaymentTypeSelection
      key={componentKey}
      service={slotService}
      selectedPaymentType={selectedPaymentType}
      paymentTypes={service.paymentTypes}
    />
  );
};
